<template>
  <div class="chat-input-container">
    <v-row
      no-gutters
      align="center"
      :class="[
        'chat-input',
        {
          'chat-input--focused': focused,
          'chat-input--disabled': !socketConnected || editingActive
        }
      ]"
    >
      <v-col
        class="input-col"
      >
        <textarea
          ref="textarea"
          v-model="message"
          class="message-input"
          rows="1"
          :disabled="inputDisabled"
          placeholder="With what can I help you today?"
          @input="resizeTextarea"
          @focus="focused = true"
          @blur="focused = false"
          @keydown.enter.exact.prevent="send"
          @keydown.enter.shift.stop
        />
      </v-col>

      <v-col
        cols="auto"
        class="send-col"
      >
        <button
          ref="sendButton"
          type="button"
          :class="[
            'send-button',
            {
              'send-button--active': canSend && !streamingActive,
              'send-button--streaming': streamingActive && !stopRequested
            }
          ]"
          :disabled="actionDisabled"
          :aria-label="streamingActive ? 'Stop response' : 'Send message'"
          @click="handleAction"
          @mouseenter="showTooltip"
          @mouseleave="hideTooltip"
        >
          <v-icon :size="20">
            {{ streamingActive ? 'mdi-stop' : 'mdi-arrow-up' }}
          </v-icon>
        </button>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import assistantService from '@/ai/services/AssistantService'

export default {
  name: 'ChatInput',

  dataStore: {
    assistantStore: 'AssistantStore'
  },

  data () {
    return {
      message       : '',
      focused       : false,
      tooltipElement: null
    }
  },

  computed: {
    streamingActive () {
      return this.assistantStore.Streaming.Active || this.assistantStore.Streaming.AnimationActive
    },

    socketConnected () {
      return this.assistantStore.Socket.Connected
    },

    sendEnabled () {
      return this.assistantStore.Messages.SendEnabled &&
        !this.streamingActive &&
        !this.editingActive &&
        this.socketConnected
    },

    editingActive () {
      return this.assistantStore.Editing.Active
    },

    inputDisabled () {
      return !this.socketConnected || this.streamingActive
    },

    canSend () {
      return (
        this.message.trim().length > 0 && this.sendEnabled
      )
    },

    stopRequested () {
      return this.assistantStore.Streaming.StopRequested
    },

    actionDisabled () {
      if (this.editingActive) {
        return true
      }
      if (this.streamingActive) {
        return this.stopRequested || !this.socketConnected
      }

      return !this.canSend
    }
  },

  watch: {
    streamingActive () {
      if (this.tooltipElement) {
        this.showTooltip()
      }
    },
    actionDisabled (newVal) {
      if (newVal) {
        this.removeTooltip()
      }
    }
  },

  beforeDestroy () {
    this.removeTooltip()
  },

  methods: {
    handleAction () {
      if (this.editingActive) {
        return
      }
      if (this.streamingActive) {
        assistantService.stopStreaming()
        return
      }

      this.send()
    },

    send () {
      const text = this.message.trim()

      if (!text || !this.canSend) {
        return
      }

      this.message = ''

      this.$nextTick(() => {
        this.resizeTextarea()
      })

      assistantService.sendMessage(text)
    },

    resizeTextarea () {
      const textarea = this.$refs.textarea

      if (!textarea) {
        return
      }

      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 132)}px`
    },

    showTooltip () {
      if (this.actionDisabled) {
        return
      }

      const button = this.$refs.sendButton
      const text = this.streamingActive ? 'Stop Response' : 'Submit'

      if (!button) return

      this.removeTooltip()

      const tooltip = document.createElement('div')
      tooltip.className = 'chat-input-tooltip'
      tooltip.textContent = text

      const rect = button.getBoundingClientRect()
      tooltip.style.cssText = `
        position: fixed;
        top: ${rect.bottom + 8}px;
        left: ${rect.left + rect.width / 2}px;
        transform: translateX(-50%);
        padding: 3px 8px;
        background: white;
        color: #041f30;
        font-family: 'Inter';
        font-size: 11px;
        font-weight: 400;
        line-height: 21px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        white-space: nowrap;
        z-index: 99999;
        pointer-events: none;
        opacity: 0;
        animation: tooltipFadeIn 0.15s ease forwards;
      `

      document.body.appendChild(tooltip)
      this.tooltipElement = tooltip
    },

    hideTooltip () {
      this.removeTooltip()
    },

    removeTooltip () {
      if (this.tooltipElement) {
        this.tooltipElement.remove()
        this.tooltipElement = null
      }
    }
  }
}
</script>

<style scoped>
.chat-input-container {
  padding-left: 12px;
  padding-right: 12px;
  padding-bottom: 24px;
  width: 100%;
  min-width: 0;
  margin: 0;
  box-sizing: border-box;
}

.chat-input {
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 80px;
  padding: 10px 10px 10px 16px;
  border: 0;
  border-radius: 22px;
  background: linear-gradient(180deg, #d4deee 0%, #dbe4f2 18%, #e6ecf8 34%, #e7ecfa 100%);
  box-shadow: 0 12px 40px 0 #3D68D53D;
  backdrop-filter: blur(3px);
  overflow: hidden;
  box-sizing: border-box;
  transition: box-shadow 0.15s ease;
  column-gap: 8px;
}

.chat-input::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow:
    inset 0 0 0 4px rgba(255, 255, 255, 0.92),
    inset 0 2px 0 rgba(255, 255, 255, 0.62),
    inset 0 -1px 0 rgba(255, 255, 255, 0.24);
}

.chat-input--focused::before {
  box-shadow:
    inset 0 0 0 4px rgba(255, 255, 255, 0.92),
    inset 0 2px 0 rgba(255, 255, 255, 0.72),
    inset 0 -1px 0 rgba(255, 255, 255, 0.3);
}

.chat-input--focused {
  box-shadow:
    0 14px 28px rgba(70, 108, 178, 0.28),
    0 5px 12px rgba(55, 89, 149, 0.2);
}

.chat-input--disabled {
  opacity: 0.72;
  filter: saturate(0.88);
}

.input-col {
  min-width: 0;
}

.message-input {
  width: 100%;
  min-width: 0;
  min-height: 44px;
  max-height: 132px;
  padding: 11.5px 0;
  border: 0;
  outline: none;
  background: transparent;
  color: #041F30;
  font-family: 'Inter';
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  resize: none;
  overflow-y: auto;
}

.message-input::placeholder {
  color: #515F67;
  font-family: 'Inter';
  font-weight: 400;
  line-height: 21px;
  opacity: 1;
}

.message-input:placeholder-shown {
  padding: 11.5px 0;
  line-height: 21px; /* center placeholder vertically */
}

.message-input::-webkit-scrollbar {
  width: 6px;
}

.send-col {
  display: flex;
  align-items: center;
  align-self: center;
}

.chat-input.no-gutters > .send-col {
  padding-right: 14px !important;
  padding-left: 0 !important;
}

.send-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0;
  padding: 0;
  line-height: 1;
  border: 0;
  border-radius: 8px;
  background: #FFFFFF;
  color: #CFD9F4;
  cursor: default;
  transition:
    background-color 0.15s ease,
    transform 0.15s ease,
    opacity 0.15s ease;
}

.send-button--active {
  background: #93DE94;
  color: #041F30;
  cursor: pointer;
}

.send-button--streaming {
  background: #CFD9F4;
  color: #ffffff;
  cursor: pointer;
}

.send-button--streaming:hover {
  background: #ffffff;
  color: #CFD9F4;
  cursor: pointer;
}

.send-button--active:hover:not(:disabled),
.send-button--streaming:hover:not(:disabled) {
  transform: translateY(-1px);
}

.send-button:disabled {
  background: #FFFFFF;
  color: #CFD9F4;
  cursor: default;
  opacity: 1;
}

.send-button .v-icon {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 24px;
  font-size: 18px !important;
  line-height: 24px !important;
  margin: 0 !important;
  color: inherit !important;
}

.send-button:disabled .v-icon {
  color: #c2cde8 !important;
}

.send-button--streaming .v-icon {
  width: 24px;
  height: 24px;
  font-size: 24px !important;
  line-height: 24px !important;
}
</style>

<style>
@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
</style>
